import React, { useState, useEffect } from "react";
import { Search, UserPlus, Loader2, Users, RefreshCw } from "lucide-react";
import { useTranslation, useFormat } from "../i18n";

const WorkvivoPeople = () => {
  const { t } = useTranslation("workvivo");
  const fmt = useFormat();
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch Workvivo people
    const fetchPeople = async () => {
      setLoading(true);
      setError(null);

      try {
        // In production, this would be an actual API call to your workvivoService
        // Simulating API response for demonstration
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Mock data - in production this would come from the API
        const mockPeople = [
          {
            id: 1,
            name: "Michael Baskhairoun",
            profilePic: "/images/Michael.jpg",
            role: "Product Manager",
            department: "Product",
            location: "San Francisco, CA",
            lastActive: new Date()
          },
          {
            id: 2,
            name: "Rofaiel Tadrous",
            profilePic: "/images/Rofa.jpeg",
            role: "Senior Engineer",
            department: "Engineering",
            location: "London, UK",
            lastActive: new Date(Date.now() - 3 * 60 * 60 * 1000)
          },
          {
            id: 3,
            name: "Raghav Bhalla",
            profilePic: "/images/Raghav.jpg",
            role: "Design Lead",
            department: "Design",
            location: "New York, NY",
            lastActive: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
          },
          {
            id: 4,
            name: "Caroline Smith",
            profilePic: "/images/Caroline.jpg",
            role: "Marketing Director",
            department: "Marketing",
            location: "Austin, TX",
            lastActive: new Date(Date.now() - 30 * 60 * 1000)
          }
        ];

        setPeople(mockPeople);
      } catch (err) {
        setError(t("people.load_failed"));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter people based on search query
  const filteredPeople = people.filter(person =>
    person.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    person.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
    person.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col p-4 md:p-6 bg-gray-50 min-h-screen w-full">
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        {/* Header */}
        <div className="p-4 org-gradient flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-white" />
            <h2 className="text-xl font-bold text-white">{t("people.title")}</h2>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
            title={t("common:actions.refresh")}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t("people.search_placeholder")}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
            <p className="text-gray-600">{t("people.loading")}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{t("people.load_failed_title")}</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 org-bg-primary org-btn text-white rounded-full transition-colors"
            >
              {t("people.try_again")}
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredPeople.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <UserPlus className="w-8 h-8 text-gray-400" />
            </div>

            {searchQuery ? (
              <>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{t("people.no_match_title")}</h3>
                <p className="text-gray-600">{t("people.no_match_hint")}</p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{t("people.empty_title")}</h3>
                <p className="text-gray-600 mb-4">{t("people.empty_hint")}</p>
                <button className="px-4 py-2 org-bg-primary org-btn text-white rounded-full transition-colors">
                  {t("people.connect")}
                </button>
              </>
            )}
          </div>
        )}

        {/* People List */}
        {!loading && !error && filteredPeople.length > 0 && (
          <div className="divide-y divide-gray-200">
            {filteredPeople.map(person => (
              <div key={person.id} className="p-4 flex items-center hover:bg-gray-50 transition-colors">
                <div className="relative">
                  <img
                    src={process.env.PUBLIC_URL + person.profilePic}
                    alt={person.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                  />
                  <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                    new Date() - person.lastActive < 60 * 60 * 1000 ? 'bg-green-500' : 'bg-gray-300'
                  }`}></div>
                </div>

                <div className="ml-4 flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{person.name}</h3>
                      <p className="text-sm text-gray-600">{person.role} • {person.department}</p>
                    </div>
                    <div className="mt-1 sm:mt-0 flex items-center text-xs text-gray-500">
                      <span className="mr-2">{person.location}</span>
                      <span className="text-gray-400">•</span>
                      <span className="ml-2">{t("people.active", { time: fmt.relative(person.lastActive) })}</span>
                    </div>
                  </div>
                </div>

                <button className="ml-4 flex-shrink-0 p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors">
                  <UserPlus className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkvivoPeople;
